import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AcresAdvantageDashboard,
} from '../../core/services/acres-advantage.service';
import { SponsorContactData } from '../../core/services/sponsor-contact.service';
import { HowToBuyModalComponent } from '../../shared/how-to-buy-modal/how-to-buy-modal.component';
import { environment } from '../../../environments/environment';

/**
 * Per-category badge styling configuration
 * Maps chemical category strings to background and text colors
 */
interface CategoryBadgeStyle {
  backgroundColor: string;
  textColor: string;
}

const CATEGORY_STYLES: Record<string, CategoryBadgeStyle> = {
  'FUNGICIDE': { backgroundColor: '#198754', textColor: '#ffffff' },
  'ADJUVANT': { backgroundColor: '#0d6efd', textColor: '#ffffff' },
  'INSECTICIDE': { backgroundColor: '#dc3545', textColor: '#ffffff' },
  'HERBICIDE': { backgroundColor: '#6f42c1', textColor: '#ffffff' },
  'SURFACTANT': { backgroundColor: '#fd7e14', textColor: '#ffffff' },
  'NUTRITION': { backgroundColor: '#0aa2c0', textColor: '#ffffff' },
  'FERTILIZER': { backgroundColor: '#0aa2c0', textColor: '#ffffff' },
  'BIOLOGICAL': { backgroundColor: '#e6a000', textColor: '#212529' },
  'PGR': { backgroundColor: '#e91e8f', textColor: '#ffffff' },
  'IGR': { backgroundColor: '#e65100', textColor: '#ffffff' },
  'MITICIDE': { backgroundColor: '#795548', textColor: '#ffffff' },
};

/**
 * Dummy contact data for each sponsor in the preview dashboard.
 * Consistent with the existing dummy dashboard data approach.
 */
const DUMMY_CONTACT_DATA: Record<string, SponsorContactData> = {
  'demo-atticus': {
    contactType: 'mixed',
    contactMessage: 'Reach out to your local Atticus representative for bulk pricing and product availability.',
    locations: [
      {
        id: 'loc-1',
        sponsorId: 'demo-atticus',
        name: 'Des Moines Distribution Center',
        street1: '4500 Merle Hay Rd',
        street2: null,
        city: 'Des Moines',
        state: 'IA',
        zipCode: '50310',
        latitude: null,
        longitude: null,
        phone: '(515) 555-0120',
        email: 'desmoines@atticus-ag.example.com'
      },
      {
        id: 'loc-2',
        sponsorId: 'demo-atticus',
        name: 'Kansas City Warehouse',
        street1: '1200 W 12th St',
        street2: 'Suite 200',
        city: 'Kansas City',
        state: 'MO',
        zipCode: '64101',
        latitude: null,
        longitude: null,
        phone: '(816) 555-0140',
        email: null
      }
    ],
    representatives: [
      {
        id: 'rep-1',
        sponsorId: 'demo-atticus',
        name: 'Mike Johnson',
        title: 'Regional Sales Director',
        territory: 'Iowa, Illinois, Missouri',
        phone: '(515) 555-0199',
        email: 'mike.johnson@atticus-ag.example.com'
      },
      {
        id: 'rep-2',
        sponsorId: 'demo-atticus',
        name: 'Sarah Williams',
        title: 'Territory Manager',
        territory: 'Kansas, Nebraska, Oklahoma',
        phone: '(316) 555-0188',
        email: 'sarah.williams@atticus-ag.example.com'
      }
    ],
    sponsorName: 'Atticus',
    sponsorLogoUrl: null,
    sponsorBrandColor: '#00205b',
    sponsorWebsite: 'https://www.atticus-ag.example.com'
  },
  'demo-spraytec': {
    contactType: 'reps_only',
    contactMessage: 'Contact a spraytec representative for product recommendations and availability.',
    locations: [],
    representatives: [
      {
        id: 'rep-3',
        sponsorId: 'demo-spraytec',
        name: 'Tom Anderson',
        title: 'National Accounts Manager',
        territory: 'Midwest Region',
        phone: '(800) 555-0155',
        email: 'tom.anderson@spraytec.example.com'
      }
    ],
    sponsorName: 'spraytec',
    sponsorLogoUrl: null,
    sponsorBrandColor: '#f8f8f8',
    sponsorWebsite: 'https://www.spraytec.example.com'
  },
  'demo-evolve': {
    contactType: 'contact_form_only',
    contactMessage: 'Visit the eVOLVE website to learn about product availability and place orders online.',
    locations: [],
    representatives: [],
    sponsorName: 'eVOLVE',
    sponsorLogoUrl: null,
    sponsorBrandColor: '#171718',
    sponsorWebsite: 'https://www.evolve-ag.example.com'
  }
};

/**
 * Dummy dashboard data for UI mockup purposes.
 * TODO: Re-hook to AcresAdvantageService.getDashboardData() once
 * business decisions on calculation mode and chemical equivalence are finalized.
 */
const DUMMY_DASHBOARD: AcresAdvantageDashboard = {
  season: {
    id: 'demo-season-2026',
    label: '2026 Spray Season',
    startDate: '2026-02-01',
    endDate: '2026-11-30',
  },
  totalPointsEarned: 4780,
  eligibleRecordsUsed: 47,
  totalSprayRecords: 59,
  totalPointsMissed: 1860,
  sponsorBreakdowns: [
    {
      sponsorId: 'demo-atticus',
      sponsorName: 'Atticus',
      sponsorLogoUrl: null,
      brandColor: '#00205b',
      totalPoints: 2340,
      chemicals: [
        { chemicalId: 'c1', chemicalName: 'ACADIA ESQ', category: 'FUNGICIDE', points: 820 },
        { chemicalId: 'c2', chemicalName: 'ACRONYX 4 F', category: 'INSECTICIDE', points: 640 },
        { chemicalId: 'c3', chemicalName: 'AQUESTA 4 F', category: 'HERBICIDE', points: 520 },
        { chemicalId: 'c4', chemicalName: 'ATECTRA PRO', category: 'HERBICIDE', points: 360 },
      ],
    },
    {
      sponsorId: 'demo-spraytec',
      sponsorName: 'spraytec',
      sponsorLogoUrl: null,
      brandColor: '#f8f8f8',
      totalPoints: 1450,
      chemicals: [
        { chemicalId: 'c5', chemicalName: 'FULLTEC DRIFT GUARD', category: 'ADJUVANT', points: 580 },
        { chemicalId: 'c6', chemicalName: 'SPRAYTEC OIL+', category: 'SURFACTANT', points: 470 },
        { chemicalId: 'c7', chemicalName: 'FULLTEC MSO', category: 'ADJUVANT', points: 400 },
      ],
    },
    {
      sponsorId: 'demo-evolve',
      sponsorName: 'eVOLVE',
      sponsorLogoUrl: null,
      brandColor: '#171718',
      totalPoints: 990,
      chemicals: [
        { chemicalId: 'c8', chemicalName: 'EVOLVE NUTRIPAK', category: 'NUTRITION', points: 380 },
        { chemicalId: 'c9', chemicalName: 'BIOCHARGE MAX', category: 'BIOLOGICAL', points: 340 },
        { chemicalId: 'c10', chemicalName: 'ROOTZONE PRIME', category: 'BIOLOGICAL', points: 270 },
      ],
    },
  ],
  missedBreakdowns: [
    { chemicalName: 'ATECTRA PRO', category: 'HERBICIDE', recordCount: 8, points: 640 },
    { chemicalName: 'FULLTEC DRIFT GUARD', category: 'ADJUVANT', recordCount: 6, points: 480 },
    { chemicalName: 'EVOLVE NUTRIPAK', category: 'NUTRITION', recordCount: 5, points: 420 },
    { chemicalName: 'ACRONYX 4 F', category: 'INSECTICIDE', recordCount: 4, points: 320 },
  ],
};

/**
 * Acres Advantage Dashboard Component
 *
 * Displays the organization's Acres Advantage rewards dashboard.
 * Shows points earned from sponsor chemicals used in spray records,
 * with per-sponsor breakdowns and summary statistics.
 *
 * Currently uses hardcoded dummy data for UI mockup review.
 * Will be re-connected to the API once business decisions are finalized.
 *
 * Reference: /agent-os/specs/2026-02-11-acres-advantage/spec.md
 */
@Component({
  selector: 'app-acres-advantage',
  standalone: true,
  imports: [CommonModule, HowToBuyModalComponent],
  templateUrl: './acres-advantage.component.html',
  styleUrls: ['./acres-advantage.component.scss']
})
export class AcresAdvantageComponent implements OnInit {
  /** Dashboard data - currently using dummy data for mockup */
  dashboard = signal<AcresAdvantageDashboard | null>(null);

  /** Loading state */
  isLoading = signal<boolean>(true);

  /** Error state */
  error = signal<string | null>(null);

  /** How to Buy modal state */
  showHowToBuyModal = false;
  selectedSponsorId: string | null = null;
  dummyContactData: SponsorContactData | null = null;

  ngOnInit(): void {
    this.loadDummyData();
  }

  /**
   * Load hardcoded dummy data for UI mockup.
   * Simulates a brief loading state for realistic UX.
   *
   * TODO: Replace with loadDashboardData() using AcresAdvantageService
   * once calculation mode and chemical equivalence decisions are finalized.
   */
  private loadDummyData(): void {
    this.isLoading.set(true);
    this.error.set(null);

    // Simulate brief network delay for realistic loading skeleton
    setTimeout(() => {
      this.dashboard.set(DUMMY_DASHBOARD);
      this.isLoading.set(false);
    }, 400);
  }

  /**
   * Check if there is an active season
   */
  get hasActiveSeason(): boolean {
    return this.dashboard()?.season !== null && this.dashboard()?.season !== undefined;
  }

  /**
   * Open the How to Buy modal for a sponsor card
   */
  openSponsorDetail(sponsorId: string): void {
    this.selectedSponsorId = sponsorId;
    this.dummyContactData = DUMMY_CONTACT_DATA[sponsorId] || null;
    this.showHowToBuyModal = true;
  }

  /**
   * Close the How to Buy modal
   */
  closeSponsorDetail(): void {
    this.showHowToBuyModal = false;
    this.selectedSponsorId = null;
    this.dummyContactData = null;
  }

  /**
   * Get the CSS background color for a category badge
   */
  getCategoryBadgeColor(category: string | null): string {
    if (!category) {
      return '#6c757d';
    }
    return CATEGORY_STYLES[category.toUpperCase()]?.backgroundColor || '#6c757d';
  }

  /**
   * Get the CSS text color for a category badge (for WCAG compliance)
   */
  getCategoryTextColor(category: string | null): string {
    if (!category) {
      return '#ffffff';
    }
    return CATEGORY_STYLES[category.toUpperCase()]?.textColor || '#ffffff';
  }

  /**
   * Resolve a logo path to a full CDN URL.
   * Handles both relative paths and legacy full URLs.
   */
  getLogoFullUrl(logoUrl: string | null | undefined): string {
    if (!logoUrl) return '';
    if (logoUrl.startsWith('http')) return logoUrl;
    return `${environment.appCDN}/${logoUrl}`;
  }

  /**
   * Format a number with comma separators
   */
  formatNumber(value: number): string {
    return value.toLocaleString('en-US');
  }
}
